import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface SLAPolicy {
  id: number
  priority: string
  first_response_minutes: number
  next_response_minutes: number
  resolution_minutes: number
  is_active: boolean
}

interface TicketSLA {
  id: number
  ticket_number: string
  title: string
  priority: string
  status: string
  created_at: string
  first_response_at: string | null
  resolved_at: string | null
  assigned_agent: { agent_name: string } | null
}

const formatDuration = (mins: number) => {
  if (mins < 60) return `${mins}m`
  if (mins < 1440) return `${Math.round(mins / 60)}h`
  return `${Math.round(mins / 1440)}d`
}

const getElapsedMinutes = (from: string) => Math.floor((Date.now() - new Date(from).getTime()) / 60000)

const SLAPolicyManager: React.FC = () => {
  const [policies, setPolicies] = useState<SLAPolicy[]>([])
  const [tickets, setTickets] = useState<TicketSLA[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editValues, setEditValues] = useState({ first_response_minutes: 0, next_response_minutes: 0, resolution_minutes: 0 })
  const [error, setError] = useState('')
  const [tab, setTab] = useState<'policies' | 'breaches'>('breaches')

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      const [polRes, ticketRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/support`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'load_sla_policies' })
        }),
        fetch(`${API_BASE_URL}/api/admin/support`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'load_tickets' })
        })
      ])
      const polJson = await polRes.json()
      const ticketJson = await ticketRes.json()
      if (polJson.success) setPolicies(polJson.data || [])
      if (ticketJson.success) setTickets(ticketJson.data?.tickets || ticketJson.tickets || [])
    } catch (err: any) { setError(err.message) }
    setLoading(false)
  }

  const startEdit = (p: SLAPolicy) => {
    setEditingId(p.id)
    setEditValues({ first_response_minutes: p.first_response_minutes, next_response_minutes: p.next_response_minutes, resolution_minutes: p.resolution_minutes })
  }

  const saveEdit = async () => {
    if (!editingId) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'update_sla_policy', data: { id: editingId, ...editValues } })
      })
      if (!res.ok) { const json = await res.json(); throw new Error(json.error) }
      setEditingId(null); loadAll()
    } catch (err: any) { setError(err.message) }
  }

  const getSLAStatus = (ticket: TicketSLA) => {
    const policy = policies.find(p => p.priority === ticket.priority && p.is_active)
    if (!policy || ticket.status === 'closed' || ticket.status === 'resolved') return null
    const elapsed = getElapsedMinutes(ticket.created_at)
    const firstResponseBreached = !ticket.first_response_at && elapsed > policy.first_response_minutes
    const resolutionBreached = elapsed > policy.resolution_minutes
    const firstResponsePct = ticket.first_response_at ? 100 : Math.min(100, (elapsed / policy.first_response_minutes) * 100)
    const resolutionPct = Math.min(100, (elapsed / policy.resolution_minutes) * 100)
    return { policy, elapsed, firstResponseBreached, resolutionBreached, firstResponsePct, resolutionPct }
  }

  const breachedTickets = tickets.filter(t => {
    const sla = getSLAStatus(t)
    return sla && (sla.firstResponseBreached || sla.resolutionBreached)
  }).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

  const atRiskTickets = tickets.filter(t => {
    const sla = getSLAStatus(t)
    return sla && !sla.firstResponseBreached && !sla.resolutionBreached && (sla.firstResponsePct > 75 || sla.resolutionPct > 75)
  })

  const priorityColor: Record<string, string> = {
    urgent: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800', low: 'bg-gray-100 text-gray-700'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">SLA Policies</h3>
          <p className="text-sm text-gray-500">
            <span className="text-red-600 font-medium">{breachedTickets.length} breached</span> · <span className="text-orange-600 font-medium">{atRiskTickets.length} at risk</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab('breaches')} className={`px-3 py-1.5 rounded text-sm font-medium ${tab === 'breaches' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
            🚨 Breaches ({breachedTickets.length})
          </button>
          <button onClick={() => setTab('policies')} className={`px-3 py-1.5 rounded text-sm font-medium ${tab === 'policies' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
            📋 Policies
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}
        <button onClick={() => setError('')} className="ml-2 font-bold">×</button></div>}

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div></div>
      ) : tab === 'policies' ? (
        <div className="space-y-3">
          {policies.map(p => (
            <div key={p.id} className={`bg-white border rounded-lg p-4 ${!p.is_active ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${priorityColor[p.priority] || 'bg-gray-100'}`}>{p.priority.toUpperCase()}</span>
                  {!p.is_active && <span className="text-sm text-white">Disabled</span>}
                </div>
                {editingId === p.id ? (
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="text-green-600 hover:text-green-800 text-sm font-medium">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => startEdit(p)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                )}
              </div>
              {editingId === p.id ? (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-500">First Response (min)</label>
                    <input type="number" min={1} value={editValues.first_response_minutes} onChange={e => setEditValues({ ...editValues, first_response_minutes: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Next Response (min)</label>
                    <input type="number" min={1} value={editValues.next_response_minutes} onChange={e => setEditValues({ ...editValues, next_response_minutes: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Resolution (min)</label>
                    <input type="number" min={1} value={editValues.resolution_minutes} onChange={e => setEditValues({ ...editValues, resolution_minutes: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-gray-500">First Response:</span> <span className="font-medium">{formatDuration(p.first_response_minutes)}</span></div>
                  <div><span className="text-gray-500">Next Response:</span> <span className="font-medium">{formatDuration(p.next_response_minutes)}</span></div>
                  <div><span className="text-gray-500">Resolution:</span> <span className="font-medium">{formatDuration(p.resolution_minutes)}</span></div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {breachedTickets.length === 0 && atRiskTickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">✅ No SLA breaches or at-risk tickets</div>
          ) : (
            <>
              {breachedTickets.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-700 mb-2">🚨 SLA Breached ({breachedTickets.length})</h4>
                  {breachedTickets.map(t => {
                    const sla = getSLAStatus(t)!
                    return (
                      <div key={t.id} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${priorityColor[t.priority]}`}>{t.priority}</span>
                            <span className="text-sm font-medium text-gray-900">{t.ticket_number}</span>
                            <span className="text-sm text-gray-600 truncate max-w-xs">{t.title}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            {sla.firstResponseBreached && <span className="text-red-600 font-medium">⏰ No first response ({formatDuration(sla.elapsed)} elapsed, target {formatDuration(sla.policy.first_response_minutes)})</span>}
                            {sla.resolutionBreached && <span className="text-red-600 font-medium">⏰ Resolution overdue ({formatDuration(sla.elapsed)} elapsed, target {formatDuration(sla.policy.resolution_minutes)})</span>}
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          Status: {t.status} · Agent: {t.assigned_agent?.agent_name || 'Unassigned'}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {atRiskTickets.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-orange-700 mb-2 mt-4">⚠️ At Risk ({atRiskTickets.length})</h4>
                  {atRiskTickets.map(t => {
                    const sla = getSLAStatus(t)!
                    return (
                      <div key={t.id} className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${priorityColor[t.priority]}`}>{t.priority}</span>
                            <span className="text-sm font-medium text-gray-900">{t.ticket_number}</span>
                            <span className="text-sm text-gray-600 truncate max-w-xs">{t.title}</span>
                          </div>
                          <div className="text-sm text-orange-600">
                            {Math.round(sla.firstResponsePct)}% of first response / {Math.round(sla.resolutionPct)}% of resolution target
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SLAPolicyManager

